import Phaser from 'phaser';
import { GAME_HEIGHT, SCENE } from '../../config.js';
import { makeVisitScene } from './VisitSceneBase.js';
import { visitState } from '../../state/visitState.js';
import { realtime } from '../../api/realtime.js';
import { social } from '../../api/social.js';
import { OUTDOOR_CHARACTER_SCALE, BACKYARD_PERSPECTIVE } from '../../ui/carCatalog.mjs';
import { BACKYARD_WORLD_W, BACKYARD_GATE, gardenArrivalData, floorForLevel, BACKYARD_SPAWN, BACKYARD_EXIT_SPAWN_X, BACKYARD_BIKE_PARKED_DEFAULTS, altanaForLevel, backyardBlocked, BACKYARD_X_BOUNDS, seatCentre, carAnchorAt } from '../backyardLayout.js';
import { AltanaSeats } from '../../ui/AltanaSeats.js';
import { createCarSprite, setCarDepth } from '../../ui/CarSprite.js';
import { ySortDepth } from '../../ui/ySort.js';
import { iconText } from '../../ui/iconText.js';
import { hostParkedBike } from '../../ui/hostParkedBike.js';

// Same ground-line offset the owner scene y-sorts its cars on (see BackyardScene).
const CAR_BASE_OFFSET = 60;

// The host's parked bikes, read-only (mirrors VisitMainScene's bike rendering).
function renderHostBikes(scene, view) {
  const tip = iconText(scene, 0, 0, `Rowerek: ${visitState.hostName} {bike}`, {
    fontFamily: 'system-ui', fontSize: '16px', color: '#fff',
    backgroundColor: '#000000aa', padding: { x: 8, y: 4 },
  }).setOrigin(0.5, 1).setDepth(50).setVisible(false);
  // Keyed by design id since bike plan 6, `type` the kind (the defaults are per kind).
  for (const [id, b] of Object.entries(view.bikes ?? {})) {
    if (!b?.owned || b.parked_scene !== 'BackyardScene') continue;
    const type = b.type ?? id;
    const d = BACKYARD_BIKE_PARKED_DEFAULTS[type] ?? { x: 400, y: GAME_HEIGHT - 150 };
    // Same sizing as the owner's BikeController outdoors: through the ground line, at the
    // Podwórko's perspective (a host's bike is read-only, drawn once).
    hostParkedBike(scene, type, b, { x: d.x, defaultY: d.y, profile: BACKYARD_PERSPECTIVE, tip });
  }
}

// The host's cars, standing in the garage — same anchors as the owner's, read-only.
// The visit payload only carries garage cars (street cars belong to the street scene).
function renderHostCars(scene, view) {
  for (const o of scene._hostCars ?? []) o.destroy();
  scene._hostCars = [];
  const level = view?.backyard?.level ?? 1;
  const tip = iconText(scene, 0, 0, `Auto: ${visitState.hostName} {car}`, {
    fontFamily: 'system-ui', fontSize: '16px', color: '#fff',
    backgroundColor: '#000000aa', padding: { x: 8, y: 4 },
  }).setOrigin(0.5, 1).setDepth(50).setVisible(false);
  (view?.cars ?? []).forEach((c, i) => {
    const anchor = carAnchorAt(level, i);
    if (!anchor) return;
    const cont = createCarSprite(scene, c.model, 'front', anchor.x, anchor.y, { plate: c.plate });
    scene._hostCars.push(cont);
    setCarDepth(cont, ySortDepth(anchor.y + CAR_BASE_OFFSET));
    const w = cont.img?.displayWidth ?? 300;
    const h = cont.img?.displayHeight ?? 200;
    if (c.broken) {
      // Badge over the roof — a bare emoji at the sprite's corner reads as scenery.
      const wrench = iconText(scene, 0, -h / 2 + 18, '{wrench}', {
        fontFamily: 'system-ui', fontSize: '22px',
        backgroundColor: '#000000aa', padding: { x: 6, y: 2 },
      }).setOrigin(0.5);
      cont.add(wrench);
    }
    cont.setInteractive(new Phaser.Geom.Rectangle(-w / 2, -h / 2, w, h), Phaser.Geom.Rectangle.Contains);
    cont.on('pointerover', () => tip.setPosition(cont.x, cont.y - h / 2 - 6).setVisible(true));
    cont.on('pointerout', () => tip.setVisible(false));
  });
  scene._hostCars.push(tip);
}

// The visit read the host's state when it began, so a car they have driven off in since still stood in the garage
// (owner 2026-11-02: "when a person takes their car for a ride, it shouldn't be parked in podwórko"). The cars are
// read again on the way in, and whenever the host leaves this Podwórko ("Jedź" takes them out onto the street) or
// comes back into it (parked in the garage again).
async function refreshHostCars(scene) {
  const hostId = visitState.hostId;
  let fresh;
  try { fresh = await social.visit(hostId); } catch { return; }
  if (!scene.sys.isActive() || visitState.hostId !== hostId || !visitState.view) return;
  visitState.view.cars = fresh?.cars ?? [];
  renderHostCars(scene, visitState.view);
}

function watchHostCars(scene) {
  scene._hostCars = [];
  refreshHostCars(scene);
  const room = `house:${visitState.hostId}:BackyardScene`;
  const offs = [
    realtime.on('leave', (m) => { if (m.room === room && m.peerId === visitState.hostId) refreshHostCars(scene); }),
    realtime.on('join', (m) => { if (m.room === room && m.peer?.id === visitState.hostId) refreshHostCars(scene); }),
  ];
  scene.events.once('shutdown', () => offs.forEach((off) => off()));
}

// Host's Podwórko — same floor, background level and decorations as the owner scene.
// Reached from the right edge of VisitMainScene (only when the host unlocked it).
export const VisitBackyardScene = makeVisitScene({
  key: SCENE.VISIT_BACKYARD,
  roomScene: 'BackyardScene',
  areaKey: 'backyard',
  yard: { side: 'back', level: (view) => view?.backyard?.level ?? 1 },   // the host's level, under the live sky
  spawn: BACKYARD_SPAWN,
  worldWidth: BACKYARD_WORLD_W,
  worldScale: OUTDOOR_CHARACTER_SCALE,
  perspective: BACKYARD_PERSPECTIVE,
  floorPolyline: (view) => floorForLevel(view?.backyard?.level ?? 1),
  decorate: (scene, view) => { renderHostBikes(scene, view); watchHostCars(scene); renderHostCars(scene, view); },
  blocked: (view) => backyardBlocked(view?.backyard?.level ?? 1),
  xBounds: BACKYARD_X_BOUNDS,
  // Snap a seated peer's broadcast {seat} to the altana anchor for the host's *current*
  // level (not the level the peer sat down at) — same lookup AltanaSeats itself uses.
  presenceOpts: (view, _scene) => ({
    seatAnchor: (seat) => seatCentre(altanaForLevel(view?.backyard?.level ?? 1), seat),
  }),
  after: (scene, view) => {
    scene.altanaSeats = new AltanaSeats(scene, {
      natalka: scene.natalka,
      walker: scene.walker,
      getLevel: () => view?.backyard?.level ?? 1,
      getPeers: () => scene._presence ? scene._presence.avatars.values() : [],
      onBroadcast: (p) => realtime.move(p),
    });
  },
  onUpdate: (scene) => scene.altanaSeats?.update(),
  exits: [
    { side: 'left', toScene: SCENE.VISIT_MAIN, label: '{arrow_left} Przed dom', data: { spawnX: BACKYARD_EXIT_SPAWN_X } },
    { side: 'rect', rect: BACKYARD_GATE.zone, labelAt: BACKYARD_GATE.label, toScene: SCENE.VISIT_FARM, label: '{arrow_up} Ogród', data: gardenArrivalData() },
  ],
});
